import React, { useState, useEffect, useMemo, useRef } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import DefaultButton from "../../../components/buttons/defaultButton";
import DefaultInput from "../../../components/inputs/defaultInput";
import DefaultSearchSelectInput from "../../../components/inputs/defaultSearchSelectInput";
import DefaultTextarea from "../../../components/inputs/defaultTextarea";
import DefaultDatePicker from "../../../components/inputs/defaultDatePicker";
import ReduxFileUploader from "../../../components/inputs/reduxFileUploader";
import {
  createExpense,
  updateExpense,
  getExpenseCategories,
  checkPolicyCompliance,
} from "../../../../services/workspace/reimbursement.service";
import { toast } from "react-hot-toast";
import { FiUpload, FiCheck, FiAlertCircle } from "react-icons/fi";
import DefaultBadge from "../../../components/badge/defaultBadge";

const ExpenseSubmissionForm = ({ mode = "add", initialData = null, onSuccess, onCancel }) => {
  const [categories, setCategories] = useState([]);
  const [categoryData, setCategoryData] = useState({}); // Store full category data
  const [policyWarning, setPolicyWarning] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  // Store actual File object in ref (since File objects can't be serialized in Formik state)
  const receiptFileRef = useRef(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await getExpenseCategories();
      const categoryOptions = (response?.data || []).map((cat) => ({
        value: cat.id,
        label: cat.name,
        limit: cat.limit_per_transaction,
        requires_receipt: cat.requires_receipt !== false, // Default to true if not specified
      }));
      setCategories(categoryOptions);
      
      // Store full category data for quick lookup
      const categoryMap = {};
      (response?.data || []).forEach((cat) => {
        categoryMap[cat.id] = cat;
      });
      setCategoryData(categoryMap);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Failed to fetch categories:", error);
      }
      // Silently fail - categories will be empty, user can still use form
    }
  };

  // Dynamic validation schema based on category
  // Recreate schema when categories or categoryData changes
  const validationSchema = useMemo(() => {
    return Yup.object({
      category_id: Yup.string()
        .transform((value, originalValue) => {
          // If it's an object, extract the value
          if (typeof originalValue === 'object' && originalValue !== null) {
            return originalValue.value || originalValue;
          }
          return value;
        })
        .required("Category is required"),
      expense_date: Yup.date().required("Expense date is required"),
      amount: Yup.number()
        .positive("Amount must be positive")
        .required("Amount is required"),
      vendor: Yup.string().required("Vendor name is required"),
      description: Yup.string().required("Description is required"),
      receipt: Yup.array()
        .nullable() // Allow null values
        .test("receipt-required", "Receipt is required for this category", function(value) {
          // Not required for edit mode
          if (mode === "edit") return true;
          
          // Get category_id from parent
          const categoryId = this.parent.category_id;
          if (!categoryId) {
            // If no category selected yet, don't validate receipt
            return true;
          }
          
          // Get category ID (handle both string and object)
          const catId = typeof categoryId === 'object' && categoryId !== null
            ? categoryId.value || categoryId
            : String(categoryId);
          
          // Use the same lookup logic as getSelectedCategory
          const catIdStr = String(catId);
          const catIdNum = Number(catId);
          
          // Try multiple lookup methods - access from closure
          let category = categoryData[catIdStr] || categoryData[catIdNum] || categoryData[catId];
          
          if (!category) {
            category = categories.find(cat => 
              String(cat.value) === catIdStr || 
              cat.value === catIdNum || 
              cat.value === catId
            );
          }
          
          // If category not found, default to required (safety)
          if (!category) {
            if (process.env.NODE_ENV === 'development') {
              console.warn("Category not found in validation, defaulting to required", {
                catId,
                catIdStr,
                catIdNum,
                categoryDataKeys: Object.keys(categoryData),
                categoriesValues: categories.map(c => c.value)
              });
            }
            // If category not found, require receipt (safety)
            return Array.isArray(value) && value.length > 0;
          }
          
          // Check requires_receipt - handle both boolean and undefined
          // IMPORTANT: If requires_receipt is explicitly false, receipt is optional
          const requiresReceipt = category.requires_receipt;
          
          // If category explicitly says receipt is not required (false), allow null/undefined/empty
          if (requiresReceipt === false) {
            // Receipt is optional - always return true (validation passes)
            if (process.env.NODE_ENV === 'development') {
            }
            return true;
          }
          
          // If requires_receipt is true or undefined (default), receipt is required
          // Check if value is an array with at least one file
          if (Array.isArray(value) && value.length > 0) {
            // Check if array has a valid file (either _fileObject or uploadUrl/path)
            const hasValidFile = value.some(file => 
              file._fileObject || file.uploadUrl || file.path || file.localpreview
            );
            return hasValidFile;
          }
          
          // If value is null, undefined, or empty array and receipt is required, fail validation
          if (!value || (Array.isArray(value) && value.length === 0)) {
            // Create custom error message
            return this.createError({
              message: "Receipt is required for this category"
            });
          }
          
          // If it's not a valid file array and receipt is required, fail validation
          return false;
        }),
    });
  }, [categories, categoryData, mode]); // Recreate when categories change

  const getCellValue = (field) => {
    const value = initialData?.[field];
    if (value && typeof value === "object" && "data" in value) return value.data;
    return value;
  };

  const normalizeDateValue = (value) => {
    if (!value || value === "-") return "";
    if (typeof value === "string" && value.includes("T")) {
      return value.split("T")[0];
    }
    return value;
  };

  const initialCategoryId =
    initialData?.category_id ??
    initialData?.category?.id ??
    initialData?._raw?.category?.id ??
    "";

  const initialReceiptUrl =
    initialData?.receipt_url ||
    initialData?._raw?.receipt_url ||
    initialData?.receipt?.url ||
    "";

  const initialReceiptFiles =
    mode === "edit" && initialReceiptUrl
      ? [
          {
            name: "Existing receipt",
            uploadUrl: initialReceiptUrl,
            localpreview: initialReceiptUrl,
            loading: false,
          },
        ]
      : [];

  const initialValues = {
    category_id: initialCategoryId ? String(initialCategoryId) : "",
    expense_date: normalizeDateValue(getCellValue("expense_date")),
    amount: getCellValue("amount") || "",
    vendor: getCellValue("vendor") || "",
    description: getCellValue("description") || initialData?._raw?.description || "",
    receipt: initialReceiptFiles,
    notes: getCellValue("notes") || initialData?._raw?.notes || "",
  };

  const initialComparableValues = {
    category_id: initialCategoryId ? String(initialCategoryId) : "",
    expense_date: normalizeDateValue(getCellValue("expense_date")),
    amount: String(getCellValue("amount") || ""),
    vendor: String(getCellValue("vendor") || ""),
    description: String(getCellValue("description") || initialData?._raw?.description || ""),
    notes: String(getCellValue("notes") || initialData?._raw?.notes || ""),
  };

  useEffect(() => {
    if (mode !== "edit") {
      setReceiptPreview(null);
      return;
    }
    setReceiptPreview(initialReceiptUrl || null);
  }, [mode, initialData]);

  const handleReceiptUpload = (files) => {
    if (!files || !Array.isArray(files) || files.length === 0) {
      setReceiptPreview(null);
      receiptFileRef.current = null; // Clear ref
      return;
    }

    // Get first file from array
    const file = files[0];
    
    // Store the actual File object in ref (for form submission)
    if (file._fileObject && file._fileObject instanceof File) {
      receiptFileRef.current = file._fileObject;
    } else {
      receiptFileRef.current = null;
    }
    
    // If file has localpreview (from ReduxFileUploader), use it
    if (file.localpreview) {
      setReceiptPreview(file.localpreview);
      return;
    }

    // If file has _fileObject (File object), create preview
    if (file._fileObject && file._fileObject instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file._fileObject);
    }
  };

  const checkPolicy = async (categoryId, amount) => {
    if (!categoryId || !amount) return;

    try {
      const response = await checkPolicyCompliance({
        category_id: categoryId,
        amount: amount,
      });

      if (!response?.data?.is_compliant) {
        setPolicyWarning(response?.data?.warning || null);
      } else {
        setPolicyWarning(null);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Policy check failed:", error);
      }
      // Silently fail policy check - don't block user
      setPolicyWarning(null);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Get selected category to check receipt requirement
      const categoryId = typeof values.category_id === 'object' && values.category_id !== null
        ? values.category_id.value || values.category_id
        : String(values.category_id);
      
      // Use improved lookup like getSelectedCategory
      const catIdStr = String(categoryId);
      const catIdNum = Number(categoryId);
      const selectedCategory = categoryData[catIdStr] || categoryData[catIdNum] || categoryData[categoryId] 
        || categories.find(cat => String(cat.value) === catIdStr || cat.value === catIdNum || cat.value === categoryId);
      
      const receiptRequired = selectedCategory?.requires_receipt !== false;
      
      // Validate receipt requirement before submission
      const receiptFiles = Array.isArray(values.receipt) ? values.receipt : [];
      const hasReceipt = receiptFiles.length > 0 && receiptFiles.some(file => 
        file._fileObject || file.uploadUrl || file.path || file.localpreview
      );
      
      if (mode === "add" && receiptRequired && !hasReceipt) {
        toast.error("Receipt is required for this category");
        setSubmitting(false);
        return;
      }
      
      const formData = new FormData();

      // For edit: only send changed fields.
      // For add: send all non-empty fields.
      const currentComparableValues = {
        category_id: String(categoryId || ""),
        expense_date: normalizeDateValue(values.expense_date),
        amount: String(values.amount ?? ""),
        vendor: String(values.vendor ?? ""),
        description: String(values.description ?? ""),
        notes: String(values.notes ?? ""),
      };

      const changedFields = {};
      Object.keys(currentComparableValues).forEach((key) => {
        const current = currentComparableValues[key];
        const initial = String(initialComparableValues[key] ?? "");
        if (mode === "add" || current !== initial) {
          changedFields[key] = current;
        }
      });

      Object.keys(changedFields).forEach((key) => {
        if (key === "category_id") {
          formData.append(key, String(changedFields[key]));
        } else if (changedFields[key] !== null && changedFields[key] !== "") {
          formData.append(key, changedFields[key]);
        }
      });
      
      // Handle receipt field - prioritize URL if file is already uploaded, otherwise use File object
      // If receipt is optional and not provided, don't append it at all (not even as null/empty)
      // This way backend won't receive the field and won't throw "receipt cannot be null" error
      const existingReceiptUrl =
        initialData?.receipt_url || initialData?._raw?.receipt_url || "";
      const currentFirstReceipt = Array.isArray(receiptFiles) ? receiptFiles[0] : null;
      const currentReceiptUrl =
        currentFirstReceipt?.uploadUrl ||
        currentFirstReceipt?.serverResponse?.uploadUrl ||
        currentFirstReceipt?.serverResponse?.url ||
        "";

      const receiptChanged =
        mode === "add"
          ? hasReceipt
          : !!(
              currentFirstReceipt?._fileObject || // new file selected
              (hasReceipt && currentReceiptUrl && currentReceiptUrl !== existingReceiptUrl)
            );

      if (hasReceipt && receiptChanged) {
        const receiptFileObj = receiptFiles[0];
        
        // Priority 1: Extract path from response (backend wants path, not full URL)
        // Check for path first (from serverResponse.data.path or serverResponse.path)
        let receiptPath = receiptFileObj?.serverResponse?.data?.path ||     // Path from nested response (priority 1)
                         receiptFileObj?.serverResponse?.path ||              // Path from direct response (priority 2)
                         receiptFileObj?.path ||                             // Path from file object (priority 3)
                         null;
        
        // If we have a full URL but no path, extract path from URL
        if (!receiptPath) {
          const fullUrl = receiptFileObj?.serverResponse?.uploadUrl || 
                         receiptFileObj?.serverResponse?.url ||
                         receiptFileObj?.serverResponse?.data?.url ||
                         receiptFileObj?.uploadUrl ||
                         null;
          
          if (fullUrl && (fullUrl.startsWith('http://') || fullUrl.startsWith('https://'))) {
            // Extract path from full URL
            // Example: "http://192.168.1.6:8000/storage/uploads/file.png" -> "uploads/file.png"
            try {
              const urlObj = new URL(fullUrl);
              let urlPath = urlObj.pathname;
              
              // Remove leading slash and "storage/" if present
              if (urlPath.startsWith('/storage/')) {
                urlPath = urlPath.replace('/storage/', '');
              } else if (urlPath.startsWith('/')) {
                urlPath = urlPath.substring(1);
              }
              
              // If path starts with "uploads/", use it as-is
              if (urlPath.startsWith('uploads/')) {
                receiptPath = urlPath;
              } else if (urlPath) {
                // If it doesn't start with "uploads/", add it
                receiptPath = urlPath.startsWith('uploads/') ? urlPath : `uploads/${urlPath}`;
              }
            } catch (e) {
              // If URL parsing fails, try to extract path manually
              const pathMatch = fullUrl.match(/\/(?:storage\/)?(uploads\/.+)$/);
              if (pathMatch) {
                receiptPath = pathMatch[1];
              }
            }
          } else if (fullUrl) {
            // If it's already a relative path, use it as-is
            receiptPath = fullUrl;
          }
        }
        
        // Clean up path: remove leading slash if present, ensure it starts with "uploads/"
        if (receiptPath) {
          if (receiptPath.startsWith('/')) {
            receiptPath = receiptPath.substring(1);
          }
          // If path doesn't start with "uploads/", add it (unless it's already a valid relative path)
          if (!receiptPath.startsWith('uploads/') && !receiptPath.startsWith('storage/')) {
            receiptPath = `uploads/${receiptPath}`;
          }
          // Remove "storage/" prefix if present
          if (receiptPath.startsWith('storage/')) {
            receiptPath = receiptPath.replace('storage/', '');
          }
        }
        
        // Check if upload is complete (not loading)
        const isUploadComplete = !receiptFileObj?.loading && 
                                (receiptFileObj?.serverResponse || receiptPath);
        
        if (receiptPath && isUploadComplete) {
          // File has been uploaded, send the path (not full URL)
          formData.append("receipt", receiptPath);
          
          if (process.env.NODE_ENV === 'development') {
          }
        }
        // If path exists but upload is still in progress, wait
        else if (receiptPath && receiptFileObj?.loading) {
          toast.error("Please wait for file upload to complete before submitting");
          setSubmitting(false);
          return;
        }
        // Priority 2: If file hasn't been uploaded yet, use File object
        else if (receiptFileRef.current && receiptFileRef.current instanceof File) {
          formData.append("receipt", receiptFileRef.current);
        }
        // Priority 3: Fallback - Try to get File from array
        else if (receiptFileObj?._fileObject && receiptFileObj._fileObject instanceof File) {
          formData.append("receipt", receiptFileObj._fileObject);
          // Also update ref for next time
          receiptFileRef.current = receiptFileObj._fileObject;
        }
        // If none of the above, file is not ready
        else {
          console.error("Receipt file not ready. Receipt files array:", receiptFiles);
          console.error("Receipt file ref:", receiptFileRef.current);
          toast.error("Receipt file is not ready. Please wait for upload to complete or select file again.");
          setSubmitting(false);
          return;
        }
      } else if (mode === "add" && receiptRequired) {
        // If required but missing, validation should have caught this
        toast.error("Receipt is required for this category");
        setSubmitting(false);
        return;
      }
      // If receipt is optional and not provided, don't append it - backend won't receive this field

      // Debug: Log FormData contents (but not the actual file data)
      if (process.env.NODE_ENV === 'development') {
        const formDataEntries = Array.from(formData.entries()).map(([key, value]) => {
          if (value instanceof File) {
            return [key, `[File: ${value.name}, ${value.size} bytes, ${value.type}]`];
          }
          return [key, value];
        });
      }

      if (mode === "add") {
        await createExpense(formData);
        toast.success("Expense created successfully!");
      } else {
        if ([...formData.keys()].length === 0) {
          toast("No changes to update.");
          setSubmitting(false);
          return;
        }
        const expenseId = initialData?.id?.data || initialData?.id || initialData?.uuid;
        await updateExpense(expenseId, formData);
        toast.success("Expense updated successfully!");
      }

      onSuccess();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Expense submission error:", error);
        console.error("Error response:", error?.response?.data);
      }
      
      // Get category info for better error messages
      const categoryId = typeof values.category_id === 'object' && values.category_id !== null
        ? values.category_id.value || values.category_id
        : String(values.category_id);
      const selectedCategory = categoryData[categoryId] || categories.find(cat => String(cat.value) === String(categoryId));
      const receiptRequired = selectedCategory?.requires_receipt !== false;
      
      // Check if error is about receipt
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || `Failed to ${mode} expense`;
      const errorLower = errorMessage.toLowerCase();
      
      if ((errorLower.includes("receipt") && (errorLower.includes("null") || errorLower.includes("required"))) || 
          error?.response?.status === 422) {
        
        // Backend is complaining about receipt
        if (selectedCategory?.requires_receipt === false) {
          // Category says receipt is optional, but backend is rejecting
          toast.error(
            "⚠️ Backend Error: This category allows optional receipts, but backend validation is still requiring it. Please upload a receipt for now, or contact backend developer. (Backend needs to check category.requires_receipt field)",
            {
              duration: 10000,
            }
          );
        } else {
          toast.error("Receipt is required for this category");
        }
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Get selected category to check receipt requirement
  const getSelectedCategory = (categoryId) => {
    if (!categoryId) return null;
    
    // Extract category ID from object or use directly
    const catId = typeof categoryId === 'object' && categoryId !== null
      ? categoryId.value || categoryId
      : categoryId;
    
    // Convert to string for consistent lookup
    const catIdStr = String(catId);
    const catIdNum = Number(catId);
    
    // Try multiple lookup methods
    // First try categoryData (full API response)
    let category = categoryData[catIdStr] || categoryData[catIdNum] || categoryData[catId];
    
    // If not found, try categories array (formatted options)
    if (!category) {
      category = categories.find(cat => 
        String(cat.value) === catIdStr || 
        cat.value === catIdNum || 
        cat.value === catId
      );
    }
    
    return category;
  };

  // Check if receipt is required for selected category
  const isReceiptRequired = (categoryId) => {
    if (mode === "edit") return false; // Not required for edit
    const category = getSelectedCategory(categoryId);
    
    if (!category) return true; // Default to required if category not found
    
    // Check requires_receipt from categoryData (full API object) or categories array
    const requiresReceipt = category.requires_receipt !== undefined 
      ? category.requires_receipt 
      : (category.requires_receipt !== false); // Default to true if undefined
    
    return requiresReceipt !== false; // Return true unless explicitly false
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, submitCount, setFieldValue, setFieldTouched, isSubmitting, validateField }) => {
        const selectedCategory = getSelectedCategory(values.category_id);
        const receiptRequired = isReceiptRequired(values.category_id);
        const shouldShowError = (field) =>
          Boolean(errors[field] && (touched[field] || submitCount > 0));
        
        return (
        <Form className="space-y-4">
          {/* Receipt Upload Section */}
          <div>
            <ReduxFileUploader
              label={`Upload Receipt ${receiptRequired ? "*" : "(Optional)"}`}
              accept={["jpg", "jpeg", "png", "pdf"]}
              limit={1}
              multiple={false}
              onChange={(files) => {
                setFieldValue("receipt", files || []);
                handleReceiptUpload(files);
              }}
              value={Array.isArray(values.receipt) ? values.receipt : []}
              info="Supported formats: JPG, JPEG, PNG, PDF. Max size: 10 MB."
            />

            {receiptPreview && (
              <div className="mt-3">
                <img
                  src={receiptPreview}
                  alt="Receipt preview"
                  className="w-full h-40 object-contain rounded-lg border border-gray-200"
                />
              </div>
            )}

            {shouldShowError("receipt") && (
              <p className="mt-2 text-sm text-red-600">{errors.receipt}</p>
            )}
            
            {/* Show receipt requirement info based on category */}
            {selectedCategory && (
              <div className="mt-2">
                <p className="text-xs text-gray-500">
                  {receiptRequired 
                    ? "✓ Receipt is required for this category"
                    : "ℹ Receipt is optional for this category"}
                </p>
              </div>
            )}
          </div>

          {/* Category */}
          <DefaultSearchSelectInput
            label="Expense Category"
            name="category_id"
            options={categories}
            value={
              categories.find(
                (cat) => String(cat.value) === String(values.category_id)
              ) || null
            }
            onChange={async (selectedOption) => {
              // Extract value from option object if it's an object, otherwise use as-is
              const categoryId = typeof selectedOption === 'object' && selectedOption !== null
                ? selectedOption.value || selectedOption
                : selectedOption;
              
              setFieldValue("category_id", categoryId);
              
              // Get category to check receipt requirement
              const category = getSelectedCategory(categoryId);
              
              // Clear receipt if category doesn't require it
              if (category && category.requires_receipt === false && 
                  Array.isArray(values.receipt) && values.receipt.length > 0) {
                setFieldValue("receipt", []);
                setReceiptPreview(null);
                receiptFileRef.current = null; // Clear ref
              }
              
              // Re-validate receipt field when category changes
              // Use setTimeout to ensure category_id is set first
              setTimeout(() => {
                validateField("receipt");
              }, 0);
              
              checkPolicy(categoryId, values.amount);
            }}
            onBlur={() => setFieldTouched("category_id", true)}
            error={shouldShowError("category_id") ? errors.category_id : ""}
            touched={touched.category_id || submitCount > 0}
            placeholder="Select category"
          />

          {/* Expense Date */}
          <DefaultDatePicker
            label="Expense Date"
            name="expense_date"
            value={values.expense_date}
            onChange={(date) => setFieldValue("expense_date", date)}
            onBlur={() => setFieldTouched("expense_date", true)}
            error={shouldShowError("expense_date") ? errors.expense_date : ""}
            touched={touched.expense_date || submitCount > 0}
            placeholder="Select expense date"
            maxDate={true} // Prevent future dates
          />

          {/* Amount */}
          <DefaultInput
            label="Amount (₹)"
            name="amount"
            type="number"
            step="0.01"
            value={values.amount}
            onChange={(e) => {
              setFieldValue("amount", e.target.value);
              checkPolicy(values.category_id, e.target.value);
            }}
            onBlur={() => setFieldTouched("amount", true)}
            error={shouldShowError("amount") ? errors.amount : ""}
            touched={touched.amount || submitCount > 0}
            placeholder="0.00"
          />

          {/* Policy Warning */}
          {policyWarning && (
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-start gap-2">
                <FiAlertCircle className="text-orange-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-orange-900">
                    Policy Limit Exceeded
                  </p>
                  <p className="text-xs text-orange-700 mt-1">{policyWarning}</p>
                  <p className="text-xs text-orange-600 mt-1">
                    This will require additional approval.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Vendor */}
          <DefaultInput
            label="Vendor Name"
            name="vendor"
            type="text"
            value={values.vendor}
            onChange={(e) => setFieldValue("vendor", e.target.value)}
            onBlur={() => setFieldTouched("vendor", true)}
            error={shouldShowError("vendor") ? errors.vendor : ""}
            touched={touched.vendor || submitCount > 0}
            placeholder="e.g., Amazon, Uber, etc."
          />

          {/* Description */}
          <DefaultTextarea
            label="Description"
            name="description"
            required={true}
            value={values.description}
            onChange={(e) => setFieldValue("description", e.target.value)}
            onBlur={() => setFieldTouched("description", true)}
            error={shouldShowError("description") ? errors.description : ""}
            touched={touched.description || submitCount > 0}
            placeholder="Describe the expense purpose"
            rows={3}
          />

          {/* Notes (Optional) */}
          <DefaultTextarea
            label="Additional Notes (Optional)"
            name="notes"
            value={values.notes}
            onChange={(e) => setFieldValue("notes", e.target.value)}
            placeholder="Any additional information"
            rows={2}
          />

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <DefaultButton
              name="Cancel"
              variant="outline"
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
            />
            <DefaultButton
              name={mode === "add" ? "Submit Expense" : "Update Expense"}
              variant="primary"
              type="submit"
              loading={isSubmitting}
            />
          </div>
        </Form>
        );
      }}
    </Formik>
  );
};

export default ExpenseSubmissionForm;

